/// <amd-module name="Tfs/Widget/Context" />
import { IDashboardsUserPermissionService } from "Dashboards/Common/UserPermissions";
import { IProjectData, ITfsPageService } from "Tfs/Platform/Page";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssPageService } from "VSS/Platform/Page";

export interface IDashboardsTeamData {
    id: string;
    name: string;
}

export enum RepositoryType {
    Tfvc = 0,
    Git = 1
}

export interface ITfsWidgetContextService extends IVssService {
    /**
     * @throws if there is no project in the current context.
     */
    getProject(pageContext: IVssPageContext): IProjectData;

    /**
     * @throws if user data is not available in the current context
     */
    getUserName(pageContext: IVssPageContext): string;

    /**
     * Returns true if user is admin for the dashboard.
     */
    isAdmin(pageContext: IVssPageContext): boolean;

    /**
     * Returns true if the current environment is hosted. (Azure DevOps Services)
     * @throws if page data is unavailable in the current context.
     */
    isHosted(pageContext: IVssPageContext): boolean;
}

// TODO: This contract will be folded into the WidgetHost contract, for Widgets which explicitly demand project & team context.
// Dashboard and other pages will need to pass those props explicitly, if they want to host such widgets.
class WidgetContextService extends VssService implements ITfsWidgetContextService {
    public static TfvcPathRoot = "$/";

    public getProject(pageContext: IVssPageContext): IProjectData {
        const tfsPageService = pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (!tfsPageData || !tfsPageData.project) {
            throw new Error("Couldn't fetch project information.");
        }
        return tfsPageData.project;
    }

    public getUserName(pageContext: IVssPageContext): string {
        const vssPageService: IVssPageService = pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = vssPageService.getData();
        if (!pageData || !pageData.user || !pageData.user.displayName) {
            throw new Error("Couldn't fetch user name");
        }

        return pageData.user.displayName;
    }

    public isAdmin(pageContext: IVssPageContext): boolean {
        return pageContext.getService<IDashboardsUserPermissionService>("IDashboardsUserPermissionService").CanManagePermissionForDashboards();
    }

    public isHosted(pageContext: IVssPageContext): boolean {
        const vssPageService = pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = vssPageService.getData();

        if (!pageData) {
            throw new Error("Couldn't verify if environment is hosted");
        }

        return pageData.isHosted;
    }
}

Services.add("ITfsWidgetContextService", { serviceFactory: WidgetContextService });

export function getProject(pageContext: IVssPageContext): IProjectData {
    const widgetContextService = pageContext.getService<ITfsWidgetContextService>("ITfsWidgetContextService");
    return widgetContextService.getProject(pageContext);
}

export function getUserName(pageContext: IVssPageContext): string {
    const widgetContextService = pageContext.getService<ITfsWidgetContextService>("ITfsWidgetContextService");
    return widgetContextService.getUserName(pageContext);
}

export function isAdmin(pageContext: IVssPageContext): boolean {
    const widgetContextService = pageContext.getService<ITfsWidgetContextService>("ITfsWidgetContextService");
    return widgetContextService.isAdmin(pageContext);
}

export function isHosted(pageContext: IVssPageContext): boolean {
    const widgetContextService = pageContext.getService<ITfsWidgetContextService>("ITfsWidgetContextService");
    return widgetContextService.isHosted(pageContext);
}

/**
 * Get the type of the Repo by providing the path of a file in the repo
 * tfvc paths always begin with "$/" and Git paths will never begin with that.
 */
export function getRepoType(repoPath: string): RepositoryType {
    return repoPath.indexOf(WidgetContextService.TfvcPathRoot) == 0 ? RepositoryType.Tfvc : RepositoryType.Git;
}
